import { Injectable, inject } from '@angular/core';
import { PushSubscriptionHttpService } from '@core/http/push-subscription-http.service';
import { firstValueFrom } from 'rxjs';

const PUSH_SERVICE_WORKER_URL =
  '/wp-content/themes/jip/assets/js/jip-push-sw.js';
const PUSH_CLICK_MESSAGE_TYPE = 'push-notification-click';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private readonly pushSubscriptionHttpService = inject(
    PushSubscriptionHttpService
  );

  // The profile emits again on every update; ask for the permission once per page load
  private enableAttempted = false;
  private isListeningToClicks = false;

  get isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in globalThis &&
      'Notification' in globalThis
    );
  }

  get isInstalledApp(): boolean {
    return (
      globalThis.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  }

  /**
   * Subscribes the signed-in user to push when JIP runs as an installed app
   * (added to the home screen), which makes the OS show its own
   * "allow notifications" prompt. Does nothing in a normal browser tab or
   * once the user has denied the permission.
   */
  async enableForInstalledApp(): Promise<void> {
    if (
      this.enableAttempted ||
      !this.isSupported ||
      !this.isInstalledApp ||
      Notification.permission === 'denied'
    ) {
      return;
    }

    this.enableAttempted = true;

    try {
      await this.enable();
    } catch (error) {
      console.error('Failed to enable push notifications', error);
    }
  }

  /**
   * When a notification is clicked, the push service worker focuses the tab
   * that is already open and sends it the target url, so the page navigates
   * itself instead of a new tab being opened.
   */
  listenForNotificationClicks(): void {
    if (this.isListeningToClicks || !('serviceWorker' in navigator)) {
      return;
    }

    this.isListeningToClicks = true;

    navigator.serviceWorker.addEventListener('message', event => {
      if (event.data?.type !== PUSH_CLICK_MESSAGE_TYPE) {
        return;
      }

      const targetUrl = new URL(event.data.url, globalThis.location.origin);

      if (targetUrl.origin === globalThis.location.origin) {
        this.navigateTo(targetUrl.href);
      }
    });
  }

  navigateTo(url: string): void {
    globalThis.location.assign(url);
  }

  /**
   * Endpoint of this device's push subscription, or undefined when the device
   * is not subscribed. Never asks for the permission.
   */
  async getEndpoint(): Promise<string | undefined> {
    try {
      const subscription = await this.getSubscription();

      return subscription?.endpoint;
    } catch {
      return undefined;
    }
  }

  /**
   * Drops this device's subscription so the next user to sign in on it is
   * subscribed again (enable() skips a device that is already subscribed).
   */
  async unsubscribe(): Promise<void> {
    try {
      const subscription = await this.getSubscription();

      await subscription?.unsubscribe();
    } catch (error) {
      console.error('Failed to remove the push subscription', error);
    }
  }

  private async getSubscription(): Promise<PushSubscription | null> {
    if (!this.isSupported) {
      return null;
    }

    const registration = await navigator.serviceWorker.getRegistration(
      PUSH_SERVICE_WORKER_URL
    );

    return registration ? registration.pushManager.getSubscription() : null;
  }

  private async enable(): Promise<void> {
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      return;
    }

    const registration = await navigator.serviceWorker.register(
      PUSH_SERVICE_WORKER_URL
    );

    await navigator.serviceWorker.ready;

    // Already subscribed on this device: do not register it again
    if (await registration.pushManager.getSubscription()) {
      return;
    }

    const { publicKey } = await firstValueFrom(
      this.pushSubscriptionHttpService.getVapidPublicKey()
    );
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(publicKey),
    });
    const { endpoint, keys } = subscription.toJSON();

    await firstValueFrom(
      this.pushSubscriptionHttpService.subscribe({
        endpoint,
        keys: { p256dh: keys.p256dh, auth: keys.auth },
      })
    );
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replaceAll('-', '+')
      .replaceAll('_', '/');
    const rawData = atob(base64);

    return Uint8Array.from(rawData, char => char.codePointAt(0));
  }
}
